/**
 * Clients API - Endpoints for client management
 * Connected to client-service via gateway
 */
import apiClient from './client';

// Helper removed (unused)

// ============================================
// TYPES - Client
// ============================================

export type KycStatus = 'unverified' | 'pending' | 'verified' | 'rejected';
export type ClientStatus = 'actif' | 'inactif' | 'suspendu' | 'pending_verification' | 'active';

export interface Client {
    id: string;
    prenom: string;
    nom: string;
    surnom?: string; // Ti non / Nickname
    email?: string;
    telephone?: string;
    sexe?: string;
    adresse?: string;
    ville?: string;
    pays?: string;
    dateNaissance?: string;
    lieuNaissance?: string;
    pieceIdentite?: string;
    numeroPiece?: string;
    photo?: string;
    kycStatus: KycStatus;
    statut: ClientStatus;
    onlineCreated: boolean;
    userId: string;
    comptes?: Compte[];
    createdAt: string;
    updatedAt: string;
}

// ============================================
// TYPES - Compte
// ============================================

export type TypeCompte = 'BS' | 'SOL';
export type StatutCompte = 'pending' | 'actif' | 'inactif' | 'frozen' | 'ferme';
export type Devise = 'HTG' | 'USD';
export type FrequencePaiement = 'jours' | 'semaine' | 'mois';

export interface CompteTransaction {
    id: string;
    date: string;
    type: string;
    montant: number;
    created_by_username?: string;
    status: string;
}

export interface Compte {
    id: string;
    code: string;
    type: TypeCompte;
    // Backend sends as type_compte
    type_compte?: string;
    statut: StatutCompte;
    // Backend sends as etat
    etat?: string;
    devise: Devise;
    onlineCreated: boolean;
    // Balance fields (snake_case from backend)
    soldeBS: number;
    solde_BS?: number;
    soldeS: number;
    solde_S?: number;
    soldePL: number;
    solde_PL?: number;
    solde_M?: number;
    // SOL fields (camelCase)
    dateDebut?: string;
    frequence?: FrequencePaiement;
    duree?: number;
    nbMain?: number;
    sol?: number;
    montantJours?: number;
    dateFin?: string;
    // SOL fields (snake_case from backend)
    nb_main?: string;
    duree_sol?: number;
    frequence_paiement?: FrequencePaiement;
    date_debut_sol?: string;
    montant_jours?: number;
    nb_jours_payer?: number;
    nb_jours_non_payer?: number;
    total_jours?: number;
    montant_non_payer?: number;
    touche?: number;
    difference_touche?: number;
    statut_touche?: string;
    // Last transactions (snake_case from backend)
    derniere_transaction?: CompteTransaction;
    last_6_transactions?: CompteTransaction[];
    // Client info from backend
    nom?: string;
    prenom?: string;
    client_id?: string;
    email?: string;
    tel?: string;
    // Solidaire fields
    planSol?: string;
    bonisMax?: number;
    bonisActuel?: number;
    // Mutuelle fields
    dureeM?: number;
    dateDebutM?: string;
    dateFinM?: string;
    // Placement fields
    dureeP?: number;
    dateDebutP?: string;
    // Relations
    clientId: string;
    agentId?: string;
    client?: Client;
    createdAt: string;
    updatedAt: string;
}

// ============================================
// REQUEST/RESPONSE TYPES
// ============================================

export interface AdminRegisterRequest {
    nom: string;
    prenom: string;
    surnom?: string; // Ti non / Nickname
    telephone: string;
    email?: string;
    sexe?: string;
    adresse?: string;
    ville?: string;
    pays?: string;
    dateNaissance?: string;
    frontendUrl?: string; // URL frontal dinamik pou magic link (window.location.origin)
}

export interface AdminRegisterResponse {
    success: boolean;
    message: string;
    data: {
        userId: string;
        clientId: string;
        client: {
            id: string;
            nom: string;
            prenom: string;
            telephone: string;
            email?: string;
            statut: string;
        };
        pinSetupToken: string;
        pinSetupUrl: string;
    };
}

export interface CreateCompteRequest {
    type: TypeCompte;
    clientId: string;
    devise?: Devise;
    agentId?: string;
    // SOL fields
    dateDebut?: string;
    frequence?: FrequencePaiement;
    duree?: number;
    nbMain?: number;
    sol?: number;
    // Solidaire fields
    planSol?: string;
}

export interface CreateCompteResponse {
    success: boolean;
    message: string;
    compte: Compte;
    user?: {
        id: string;
        email?: string;
        telephone: string;
    };
    client?: Client;
    pinSetupUrl?: string;
}

export interface ClientsListResponse {
    clients: Client[];
    total: number;
    page: number;
    limit: number;
}

export interface ComptesListResponse {
    comptes: Compte[];
    total: number;
    page: number;
    limit: number;
}

// ============================================
// API FUNCTIONS - Clients
// ============================================

export const clientsApi = {
    /**
     * List all clients (paginated)
     */
    async list(params?: {
        page?: number;
        limit?: number;
        search?: string;
    }): Promise<ClientsListResponse> {
        const response = await apiClient.get('/api/clients', { params });
        return response.data;
    },

    /**
     * Search clients by name, email, or phone
     */
    async search(query: string): Promise<{ clients: Client[] }> {
        const response = await apiClient.get('/api/clients/search', {
            params: { q: query }
        });
        return response.data;
    },

    /**
     * Get client by ID
     */
    async getById(id: string): Promise<{ client: Client }> {
        const response = await apiClient.get(`/api/clients/${id}`);
        return response.data;
    },

    /**
     * Admin Register - Step 1: Create User + Client
     * PIN will be configured by client via email link
     */
    async adminRegister(data: AdminRegisterRequest): Promise<AdminRegisterResponse> {
        const response = await apiClient.post('/api/clients/admin/register', data);
        return response.data;
    },

    /**
     * Update client
     */
    async update(id: string, data: Partial<AdminRegisterRequest>): Promise<{ success: boolean; client: Client }> {
        const response = await apiClient.put(`/api/clients/${id}`, data);
        return response.data;
    },

    /**
     * Activate client
     */
    async activate(id: string): Promise<{ success: boolean; message: string; client: Client }> {
        const response = await apiClient.post(`/api/clients/${id}/activate`);
        return response.data;
    },

    /**
     * Deactivate/suspend client
     */
    async deactivate(id: string): Promise<{ success: boolean; message: string; client: Client }> {
        const response = await apiClient.post(`/api/clients/${id}/deactivate`);
        return response.data;
    },

    /**
     * Suspend all: User + Client + All comptes
     */
    async suspendAll(id: string, reason?: string): Promise<{ success: boolean; message: string }> {
        const response = await apiClient.post(`/api/clients/${id}/suspend-all`, { reason });
        return response.data;
    },

    /**
     * Activate all: User + Client + All comptes
     */
    async activateAll(id: string): Promise<{ success: boolean; message: string }> {
        const response = await apiClient.post(`/api/clients/${id}/activate-all`);
        return response.data;
    },

    /**
     * Rollback registration (delete client + user)
     */
    async rollback(id: string): Promise<{ success: boolean; message: string }> {
        const response = await apiClient.delete(`/api/clients/${id}/rollback`);
        return response.data;
    },

    /**
     * Get current user's profile
     */
    async getMyProfile(): Promise<{
        success: boolean;
        client: any;
        canEditIdentity: boolean;
    }> {
        const response = await apiClient.get('/api/clients/me/profile');
        return response.data;
    },

    /**
     * Update current user's profile
     */
    async updateMyProfile(data: {
        nom?: string;
        prenom?: string;
        dateNaissance?: string;
        adresse?: string;
        ville?: string;
        pays?: string;
        imageUrl?: string;
    }): Promise<{
        success: boolean;
        client: any;
        message: string;
    }> {
        const response = await apiClient.post('/api/clients/me/profile', data);
        return response.data;
    },

    // ============================================
    // KYC VERIFICATION METHODS
    // ============================================

    /**
     * Start KYC verification process
     * Returns a URL to the Didit verification page
     * @param username - Client email or phone number
     */
    async startKycVerification(username: string): Promise<{
        success: boolean;
        verification_url: string;
    }> {
        const response = await apiClient.post('/api/clients/start-kyc', { username });
        return response.data;
    },

    /**
     * Start KYC verification by client ID
     * @param clientId - Client UUID
     */
    async startKycVerificationById(clientId: string): Promise<{
        success: boolean;
        verification_url: string;
    }> {
        const response = await apiClient.post(`/api/clients/${clientId}/kyc/start`);
        return response.data;
    },

    /**
     * Check KYC verification status (for polling)
     * @param username - Client email or phone number
     */
    async checkKycStatus(username: string): Promise<{
        kyc_status: 'not_started' | 'pending' | 'verified' | 'rejected';
        client_id: string;
        timestamp: number;
    }> {
        const response = await apiClient.get('/api/clients/kyc/status', {
            params: { username }
        });
        return response.data;
    },

    /**
     * Check KYC verification status by client ID
     * @param clientId - Client UUID
     */
    async checkKycStatusById(clientId: string): Promise<{
        kyc_status: 'not_started' | 'pending' | 'verified' | 'rejected';
        client_id: string;
        timestamp: number;
    }> {
        const response = await apiClient.get('/api/clients/kyc/status', {
            params: { client_id: clientId }
        });
        return response.data;
    },

    /**
     * Get security questions configuration status
     */
    async getSecurityQuestionsStatus(): Promise<{
        configured: boolean;
        questionsCount?: number;
    }> {
        // Fallback - endpoint may not exist yet
        try {
            const response = await apiClient.get('/api/auth/security/questions/status');
            return response.data;
        } catch {
            return { configured: true }; // Default to true to hide banner if endpoint missing
        }
    },
};

// ============================================
// API FUNCTIONS - Comptes
// ============================================

export const comptesApi = {
    /**
     * List all comptes (paginated with server-side filtering)
     */
    async list(params?: {
        page?: number;
        limit?: number;
        type?: string;
        statut?: string;
        search?: string;
        onlineCreated?: boolean;
    }): Promise<ComptesListResponse> {
        const response = await apiClient.get('/api/comptes', { params });
        return response.data;
    },

    /**
     * Search active SOL accounts only - uses dedicated endpoint
     * Hardcodes type=SOL and statut=actif on backend
     */
    async searchActiveSolAccounts(search?: string, limit = 10): Promise<{ comptes: Compte[]; total: number }> {
        const response = await apiClient.get('/api/comptes/search/sol-actif', {
            params: { search, limit }
        });
        return response.data;
    },

    /**
     * Get compte by ID
     */
    async getById(id: string): Promise<{ compte: Compte }> {
        const response = await apiClient.get(`/api/comptes/${id}`);
        return response.data;
    },

    /**
     * Update compte details (Admin modifications & migrations)
     */
    async update(id: string, data: any): Promise<{ success: boolean; compte: Compte }> {
        const response = await apiClient.put(`/api/comptes/${id}`, data);
        return response.data;
    },

    /**
     * Get full account details (including SOL calculations)
     * Matches AccountDetailsController
     */
    async getAccountDetails(code: string): Promise<Compte> {
        const response = await apiClient.post('/api/clients/account/details', { code });
        return response.data;
    },

    /**
     * Get compte by code
     */
    async getByCode(code: string): Promise<{ compte: Compte }> {
        const response = await apiClient.get(`/api/comptes/by-code/${code}`);
        return response.data;
    },

    /**
     * Get all comptes for a client
     */
    async getByClientId(clientId: string): Promise<{ comptes: Compte[] }> {
        const response = await apiClient.get(`/api/comptes/client/${clientId}`);
        return response.data;
    },

    /**
     * Admin Create Compte - Step 2: Create account for existing client
     */
    async createForAdmin(data: CreateCompteRequest): Promise<CreateCompteResponse> {
        const response = await apiClient.post('/api/comptes/admin/create', data);
        return response.data;
    },

    /**
     * Activate compte
     */
    async activate(id: string): Promise<{ success: boolean; message: string; compte: Compte }> {
        const response = await apiClient.post(`/api/comptes/${id}/activate`);
        return response.data;
    },

    /**
     * Deactivate compte
     */
    async deactivate(id: string): Promise<{ success: boolean; message: string; compte: Compte }> {
        const response = await apiClient.post(`/api/comptes/${id}/deactivate`);
        return response.data;
    },

    /**
     * Assign agent to compte
     */
    async assignAgent(id: string, agentId: string): Promise<{ success: boolean; message: string; compte: Compte }> {
        const response = await apiClient.post(`/api/comptes/${id}/assign-agent`, { agentId });
        return response.data;
    },

    /**
     * Reasiye yon kont a yon lòt ajan, oswa libere (newAgentId = null)
     */
    async reassignAgent(compteId: string, newAgentId: string | null): Promise<{ success: boolean; message: string; compte: Compte }> {
        const response = await apiClient.put(`/api/comptes/${compteId}/reassign-agent`, { newAgentId });
        return response.data;
    },

    /**
     * Lis kont asiye a ajan sa
     */
    async getAgentClients(agentId: string, page = 1, search = ''): Promise<{ success: boolean; comptes: any[]; total: number }> {
        const response = await apiClient.get(`/api/agents/${agentId}/clients`, { params: { page, search } });
        return response.data;
    },

    /**
     * Get solidaire details for a compte
     */
    async getSolidaireDetails(id: string): Promise<{ success: boolean; details: any }> {
        const response = await apiClient.get(`/api/comptes/${id}/solidaire-details`);
        return response.data;
    },

    /**
     * Get all accounts for the authenticated user (Client Dashboard)
     */
    async getMyAccounts(): Promise<{
        results: Compte[];
        count: number;
        total_pages: number;
        current_page: number;
    }> {
        const response = await apiClient.get('/api/clients/account/mine');
        return response.data;
    },

    /**
     * Get statistics for "Plan Solidè" (52 weeks)
     * Mirrors mobile app logic
     */
    async getPlanSolideStats(code: string): Promise<any> {
        const response = await apiClient.get(`/api/app/mon-plan-solide`, {
            params: { code }
        });
        return response.data;
    },

    /**
     * Get transaction history for an account (uses transaction-service)
     * @param compteId - Account UUID
     * @param options - Filter options (type, start_date, end_date, group_sol)
     */
    async getClientHistory(compteId: string, options?: {
        type?: string;
        start_date?: string;
        end_date?: string;
        group_sol?: boolean;
        page?: number;
    }): Promise<{
        results: any[];
        count: number;
        total_pages: number;
        current_page: number;
    }> {
        const params: Record<string, any> = { compte_id: compteId };
        if (options?.type) params.type = options.type;
        if (options?.start_date) params.start_date = options.start_date;
        if (options?.end_date) params.end_date = options.end_date;
        if (options?.group_sol !== undefined) params.group_sol = options.group_sol;
        if (options?.page) params.page = options.page;

        const response = await apiClient.get('/api/transactions/client/history/', { params });
        return response.data;
    },

    /**
     * Join a feature waitlist (e.g., Carte)
     */
    async joinWaitlist(data: { email?: string; phone?: string; feature?: string }): Promise<{
        success: boolean;
        message: string;
        data?: any;
    }> {
        const response = await apiClient.post('/api/clients/waitlist', {
            email: data.email || undefined,
            phone: data.phone || undefined,
            feature: data.feature || 'carte',
        });
        return response.data;
    },


    /**
     * Get security questions configuration status
     */
    async getSecurityQuestionsStatus(): Promise<{
        configured: boolean;
        questionsCount?: number;
    }> {
        // Fallback - endpoint may not exist yet
        try {
            const response = await apiClient.get('/api/auth/security/questions/status');
            return response.data;
        } catch {
            return { configured: true }; // Default to true to hide banner if endpoint missing
        }
    },
};

export default clientsApi;
